import {useEffect, useState} from 'react';

// Fired when auth/permission state in localStorage changes within this tab. The
// native 'storage' event only fires in *other* tabs, so same-tab login/logout
// must notify listeners explicitly via this event.
export const PERMISSIONS_CHANGED_EVENT = 'permissionsChanged';

export function notifyPermissionsChanged() {
    window.dispatchEvent(new Event(PERMISSIONS_CHANGED_EVENT));
}

export function usePermissionLookup() {
    const [storedPermissions, setStoredPermissions] = useState(() => localStorage.getItem('permissions') || '');
    const [isAdmin, setIsAdmin] = useState(() => localStorage.getItem('administrator') === 'true');

    useEffect(() => {
        const handleStorageChange = () => {
            setStoredPermissions(localStorage.getItem('permissions') || '');
            setIsAdmin(localStorage.getItem('administrator') === 'true');
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener(PERMISSIONS_CHANGED_EVENT, handleStorageChange);
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener(PERMISSIONS_CHANGED_EVENT, handleStorageChange);
        };
    }, []);

    const parsedPermissions = storedPermissions.split(',').filter(p => p);

    return (permission) => {
        if (isAdmin) {
            return true;
        }
        return parsedPermissions.some(userPermission =>
            doesPermissionMatch(permission, userPermission)
        );
    }
}

function doesPermissionMatch(requiredPermission, userPermission) {
    const requiredPermissionParts = requiredPermission.toLowerCase().split(":");
    const userPermissionParts = userPermission.toLowerCase().split(":");

    for (let i = 0; i < requiredPermissionParts.length; i++) {
        if (i >= userPermissionParts.length) {
            return false;
        }
        if (userPermissionParts[i] === "*") {
            return true;
        }
        if (userPermissionParts[i] !== requiredPermissionParts[i]) {
            return false;
        }
    }

    return requiredPermissionParts.length === userPermissionParts.length;
}

export function useHasPermission(permission) {
    const isAdmin = localStorage.getItem('administrator') === 'true';
    if (isAdmin) {
        return true;
    }
    const storedPermissions = localStorage.getItem('permissions') || '';
    const parsedPermissions = storedPermissions.split(',');
    for (const usersPermission of parsedPermissions) {
        if (doesPermissionMatch(permission, usersPermission)) {
            return true;
        }
    }
    return false;
}

export function useIsAdministrator() {
    const isAdmin = localStorage.getItem('administrator') === 'true';
    return isAdmin;
}